/**
 * Account-cell derivation shared by every members-app payment table (Upcoming Payments, Past
 * Transactions, Recent Transactions) — each renders `accountKind`/`network`/`accountSuffix`/
 * `bankName`/`expirationMonth`/`expirationYear` identically, matching the design system's own
 * `AccountCell` component (byte-identical across the corresponding design source files).
 */
import type { CardNetwork, PaymentAccountKind } from '@/graphql/generated/graphql';
import { formatExpiryFromParts } from './paymentAccount.format';

export type AccountDisplay =
  | { kind: 'card'; network: CardNetwork | undefined; maskedNumber: string; expiry: string | null }
  | { kind: 'bank'; label: string }
  | { kind: 'generic'; labelKey: 'check' | 'unknown' | 'removed' };

interface AccountDisplayInput {
  accountKind: PaymentAccountKind;
  network?: CardNetwork | null;
  accountSuffix?: string | null;
  bankName?: string | null;
  expirationMonth?: number | null;
  expirationYear?: number | null;
}

/**
 * Derives the account cell's chip/label from the flat account fields shared by
 * `UpcomingPayment` and `PaymentTransaction`. `CHECK`/`UNKNOWN`/`REMOVED` all render the design
 * system's generic muted-icon fallback (the same treatment `AccountCell` falls back to when it
 * can't classify an account as card or bank) — distinct label per kind. Returns a `labelKey`
 * rather than a literal string since this is a plain function with no `useTranslation` access;
 * the calling component looks the key up under `payments.account.*`.
 *
 * Cards and banks mask with four U+00B7 middle dots (`···· 1234`), the glyph the design uses and the
 * one every other payment surface already renders (`CreditCardRow`, the review screens). Not bullets,
 * and not an ellipsis.
 */
export function getAccountDisplay(payment: AccountDisplayInput): AccountDisplay {
  switch (payment.accountKind) {
    case 'CARD':
      return {
        kind: 'card',
        network: payment.network ?? undefined,
        maskedNumber: payment.accountSuffix ? `···· ${payment.accountSuffix}` : '····',
        expiry:
          payment.expirationMonth != null && payment.expirationYear != null
            ? formatExpiryFromParts(payment.expirationMonth, payment.expirationYear)
            : null,
      };
    case 'BANK':
      return {
        kind: 'bank',
        label: [payment.bankName, payment.accountSuffix ? `···· ${payment.accountSuffix}` : null]
          .filter(Boolean)
          .join(' '),
      };
    case 'CHECK':
      return { kind: 'generic', labelKey: 'check' };
    case 'UNKNOWN':
      return { kind: 'generic', labelKey: 'unknown' };
    case 'REMOVED':
      return { kind: 'generic', labelKey: 'removed' };
  }
}
